// Distribution of the work an order still owes when the supervisor undoes its review.
//
// The remainder tasks do NOT exist yet when the supervisor chooses: undo-in-review re-plans them
// inside the same call. So an assignment is keyed by {warehouse_location_id, container_id,
// pick_type} — the same key PickingTasks::ContainerBreakReconciler plans on — and never by
// task_id. Tasks already active keep their picker; only freshly created ones take an override.
const CONTAINERLESS = ''

export const taskAssignmentKey = (task) =>
    [task.warehouse_location_id, task.container_id ?? CONTAINERLESS, task.pick_type].join('|')

const productKey = (p) =>
    p.product_id != null ? `pid:${p.product_id}` : (p.upc || (p.skus || []).join(",") || "—")

// Keys whose picker the supervisor cannot actually choose, so no control is offered for them.
// The three active statuses: the re-plan only creates where nothing is active, so an override
// there is dropped. CANCELED: undo_in_review resumes a halted task (CANCELED + halted_at) to its
// ORIGINAL picker before the re-plan runs, which then skips the key; a truly cancelled task IS
// re-planned, but onto the order's own picker. halted_at is not serialized, so the two cannot be
// told apart. A halt that froze an ASSIGNED task returns it to its picker; one that froze a
// PENDING task returns it unowned, to be handed out from the processing list as before.
const NOT_REPLANNABLE = ["PENDING", "ASSIGNED", "IN_PROGRESS", "CANCELED"]

// Pending comes from the per-product breakdown, never from task.expected_quantity: a task closed
// short has that column rewritten down to what was picked, so it reads as finished. The breakdown
// is derived from the order's stock units, where the unfound pieces are still RESERVED.
const productsOf = (task) =>
    (Array.isArray(task.products) ? task.products : []).map(p => ({
        key: productKey(p),
        sku: (p.skus && p.skus.length > 0) ? p.skus.join(", ") : "—",
        name: p.name || null,
        expected: p.expected_quantity || 0,
        picked: p.picked_quantity || 0,
        pending: Math.max((p.expected_quantity || 0) - (p.picked_quantity || 0), 0),
    }))

// One row per assignment key. Several tasks can share a key — a task closed short plus the one the
// last re-plan created at the same location — and the backend resolves each one's products from
// the SAME pool of units at that location/container, so they all report the identical set. They are
// therefore merged by taking the larger figure per product, never by adding: summing would hand the
// supervisor twice the work that physically exists.
export const pendingRowsFromTasks = (tasks) => {
    const byKey = new Map()

    for (const task of (tasks || [])) {
        const key = taskAssignmentKey(task)
        let row = byKey.get(key)
        if (!row) {
            row = {
                key,
                warehouse_location_id: task.warehouse_location_id,
                container_id: task.container_id ?? null,
                pick_type: task.pick_type,
                location_code: task.location_code || null,
                container_barcode: task.container_barcode || null,
                skip: false,
                merged: [],
            }
            byKey.set(key, row)
        }
        if (NOT_REPLANNABLE.includes(task.status)) row.skip = true

        for (const product of productsOf(task)) {
            const existing = row.merged.find(p => p.key === product.key)
            if (!existing) {
                row.merged.push({ ...product })
                continue
            }
            existing.expected = Math.max(existing.expected, product.expected)
            existing.picked = Math.max(existing.picked, product.picked)
            existing.pending = Math.max(existing.expected - existing.picked, 0)
        }
    }

    return Array.from(byKey.values())
        .filter(row => !row.skip)
        .map(({ merged, ...row }) => ({
            key: row.key,
            warehouse_location_id: row.warehouse_location_id,
            container_id: row.container_id,
            pick_type: row.pick_type,
            location_code: row.location_code,
            container_barcode: row.container_barcode,
            // Whole-key totals, counting products already complete, so the progress reads the same
            // here as it does in the picking-tasks modal for this location.
            expected: merged.reduce((acc, p) => acc + p.expected, 0),
            picked: merged.reduce((acc, p) => acc + p.picked, 0),
            pending: merged.reduce((acc, p) => acc + p.pending, 0),
            products: merged.filter(p => p.pending > 0),
        }))
        .filter(row => row.pending > 0)
}

export const buildUndoInReviewPayload = ({ pickerId, assignments } = {}) => {
    const named = (assignments || [])
        .filter(a => a && a.picker_id)
        .map(a => ({
            warehouse_location_id: a.warehouse_location_id,
            // absent, not null, for loose picks — the backend only expects it on box/pallet
            ...(a.container_id ? { container_id: a.container_id } : {}),
            pick_type: a.pick_type,
            picker_id: a.picker_id,
        }))

    return named.length > 0 ? { picker_id: pickerId, assignments: named } : { picker_id: pickerId }
}
